import { Dayjs } from 'dayjs';
import { Draft } from 'immer';
import { createContext, ReactNode, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import dayjs from 'services/dayjs';
import { CombinedError, RequestPolicy, useClient, UseQueryState } from 'urql';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import {
  MultiPageInfo,
  PackhouseAllocation,
  PackhouseItemAllocations,
  PalletType,
  PackhouseDocument,
  PackhouseQuery,
  PackhouseScreen,
  RefetchType,
  usePackhousePendingQuery,
  usePackhouseQuery,
  useSubmitPackhouseItemAllocationsMutation,
  usePackhouseClearInventoriesMutation,
} from 'generated/graphql';
import { useStitchPackhouse } from '../screens/Packhouse/useStitchPackhouse';
import useDebouncedFunc from './useDebouncedFunc';
import useHasPending from './useHasPending';
import useNumberParams from './useNumberParams';
import useOfflineQuery from './useOfflineQuery';
import useProfileConfig from './useProfileConfig';
import useSearch from './useSearch';
import { SnackbarVariant, useSnackbar } from './useSnackBar';
import useUser from './useUser';

const DEBOUNCE_TIME = 800;
const ONE_SECOND = 1000;
const PACKHOUSE_DATE_PARAM = 'd';
const PACKHOUSE_DATE_FORMAT = 'YYYY-MM-DD';

/** Allocation field payload; `groupID` / `palletType` are sent for `PackhouseItemAllocations` (partial pallets). */
export type PackhouseSubmitAllocationInput = PackhouseAllocation & {
  groupID?: string;
  palletType?: PalletType;
  mixedPalletProductIDs?: string[];
};

interface PackhouseContextProps {
  hasPending: boolean;
  packhouseResult: UseQueryState<PackhouseQuery>;
  packhouseFetching?: boolean;
  packhouseSchedule?: PackhouseScreen | undefined;
  packhouseSchedulePageInfos?: MultiPageInfo;
  packhouseStartDate: Dayjs;
  setPackhouseStartDate: (packhouseStartDate: Dayjs) => void;
  refreshPackhouseResult: () => void;
  productID: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onIncreasePageVertical: () => void;
  onIncreasePageHorizontal: () => void;
  onDecreasePageHorizontal: () => void;
  onSubmitAllocation: (submitAllocation: PackhouseSubmitAllocationInput) => Promise<undefined | CombinedError>;
  onRefetchProductData: (productIDs: string[], refetchType?: RefetchType) => Promise<void>;
  onRefetchPackhouse: (requestPolicy?: RequestPolicy, ids?: string[], refetchType?: RefetchType) => void;
  onPackhouseDataChange: (updater: (draft: Draft<PackhouseScreen> | undefined) => void) => void;
  onResetPageVertHoriz: () => void;
  onClearAllInventories: () => Promise<void>;
}

const Context = createContext<PackhouseContextProps | undefined>(undefined);

export const PackhouseProvider = ({ children }: { children: ReactNode }) => {
  const [getNumberParam] = useNumberParams();
  const productID = getNumberParam('productID') ?? 0;
  const [getSearchParam, setSearchParam] = useSearch();
  const [packhouseStartDate, setPackhouseStartDate] = useState<Dayjs>(() => {
    const dateParam = getSearchParam(PACKHOUSE_DATE_PARAM);
    const parsedDate = dateParam ? dayjs(dateParam, PACKHOUSE_DATE_FORMAT, true) : null;
    return parsedDate?.isValid() ? parsedDate : dayjs();
  });
  const [searchValue, setSearchValue] = useState('');
  const { showSnackbar } = useSnackbar();
  const [verticalPageNumber, setVerticalPageNumber] = useState(DEFAULT_PAGE_NUMBER);
  const [horizontalPageNumber, setHorizontalPageNumber] = useState(DEFAULT_PAGE_NUMBER);
  const [selectedProductIDs, setSelectedProductIDs] = useState<string[]>([]);
  const [refetchType, setRefetchType] = useState(RefetchType.ProductType);

  const { user, isAdmin } = useUser();

  const { profileID } = useProfileConfig();
  const sellerID = isAdmin && profileID ? profileID : user?.profileId?.toString();

  const queryVariablesNew = useMemo(() => {
    return {
      input: {
        profileID: sellerID ?? '',
        // The server floors this to its UTC day; sending that day keeps the offline cache key stable across visits.
        packingDate: packhouseStartDate.utc().startOf('day').toISOString(),
        pagination: {
          horizontal: {
            ids: selectedProductIDs,
            pageNumber: horizontalPageNumber,
            pageSize: 10,
            type: refetchType,
          },
          vertical: {
            ids: selectedProductIDs,
            pageNumber: verticalPageNumber,
            pageSize: 25,
            type: refetchType,
          },
        },
      },
    };
  }, [packhouseStartDate, selectedProductIDs, horizontalPageNumber, verticalPageNumber, sellerID, refetchType]);

  const [packhouseResultNew, refreshPackhouseResultNew] = useOfflineQuery('packhouse-query-new', usePackhouseQuery, {
    variables: queryVariablesNew,
    requestPolicy: 'network-only',
    pause: !sellerID,
  });

  const [hasPendingResultNew, refetchHasPendingResultNew] = useOfflineQuery(
    `packhouse-pending-query-new`,
    usePackhousePendingQuery,
    {
      variables: queryVariablesNew,
      requestPolicy: 'network-only',
      pause: !sellerID,
    },
  );

  const hasPending = useMemo(
    () => Boolean(hasPendingResultNew?.data?.packhouseScreen?.hasPending),
    [hasPendingResultNew],
  );

  const onPendingResolved = useCallback(() => {
    refreshPackhouseResultNew();
  }, [refreshPackhouseResultNew]);

  const pollForPendingUpdates = useHasPending(hasPending, refetchHasPendingResultNew, onPendingResolved);

  const handleResetPageVert = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setVerticalPageNumber(DEFAULT_PAGE_NUMBER);
  }, []);

  const handleResetPageVertHoriz = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setVerticalPageNumber(DEFAULT_PAGE_NUMBER);
    setHorizontalPageNumber(DEFAULT_PAGE_NUMBER);
  }, []);

  const stitchPackhouse = useStitchPackhouse(
    packhouseResultNew.data?.packhouseScreen as PackhouseScreen | undefined,
    refetchType === RefetchType.Unknown,
  );

  const { packhouseSchedule, set: setPackhouseData, stitchSchedule: stitchScheduleScreen } = stitchPackhouse;

  const packhouseFetching = packhouseResultNew.fetching || packhouseResultNew.stale;
  const packhouseResultError = packhouseResultNew.error;

  const debouncedSearch = useDebouncedFunc((value: string) => {
    setSearchValue(value);
  }, DEBOUNCE_TIME);

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value !== searchValue) {
        handleResetPageVertHoriz();
        debouncedSearch(value);
      }
    },
    [searchValue, handleResetPageVertHoriz, debouncedSearch],
  );

  const [, submitPackhouseItemAllocations] = useSubmitPackhouseItemAllocationsMutation();
  const [, packhouseClearInventories] = usePackhouseClearInventoriesMutation();

  const handleSaveAllocation = useCallback(
    async (currentAllocation: PackhouseSubmitAllocationInput) => {
      if (!sellerID || !currentAllocation) return;
      const allocationProductIDs =
        currentAllocation.mixedPalletProductIDs && currentAllocation.mixedPalletProductIDs.length > 0
          ? currentAllocation.mixedPalletProductIDs
          : [currentAllocation.productID];
      const input: PackhouseItemAllocations = {
        sellerID,
        packedAt: currentAllocation.packedAt,
        allocations: allocationProductIDs.map((productID) => ({
          stock: currentAllocation.stock,
          allocated: currentAllocation.allocated,
          productID,
          shipmentID: currentAllocation.shipmentID,
          groupID: currentAllocation.groupID ?? String(productID),
          palletType: currentAllocation.palletType ?? PalletType.Normal,
        })),
      };
      const result = await submitPackhouseItemAllocations({ input });
      return result.error;
    },
    [sellerID, submitPackhouseItemAllocations],
  );

  const handleClearAllInventories = useCallback(async () => {
    if (!sellerID) return;
    const result = await packhouseClearInventories({ sellerID });
    if (!result.error) showSnackbar({ message: 'All inventory cleared', color: 'success' }, SnackbarVariant.ALERT);
    refreshPackhouseResultNew();
  }, [sellerID, packhouseClearInventories, showSnackbar, refreshPackhouseResultNew]);

  const handleRefetchPackhouse = useCallback(
    (requestPolicy: RequestPolicy = 'network-only', ids: string[] = [], type = RefetchType.Unknown) => {
      if (refetchType !== type || selectedProductIDs[0] !== ids[0]) {
        setRefetchType(type);
        setSelectedProductIDs(ids);
      }
      pollForPendingUpdates();
    },
    [refetchType, selectedProductIDs, pollForPendingUpdates],
  );

  const client = useClient();

  /**
   * Fetches updated dashboard data for a single product row and stitches it into the existing packhouse schedule state.
   *
   * This is used instead of re-running the full dashboard query to avoid cancelling other unresolved requests,
   * which can happen when the dashboard query variables change rapidly (e.g., during fast user input).
   *
   * By isolating the fetch and manually merging the result, we ensure targeted and stable updates to each product row.
   */
  const handleRefetchProductData = useCallback(
    async (productIDs: string[], refetchType: RefetchType = RefetchType.Product) => {
      try {
        const result = await client
          .query(
            PackhouseDocument,
            {
              input: {
                ...queryVariablesNew.input,
                pagination: {
                  horizontal: {
                    ...queryVariablesNew.input.pagination.horizontal,
                    ids: productIDs,
                    type: refetchType,
                  },
                  vertical: {
                    ...queryVariablesNew.input.pagination.vertical,
                    ids: productIDs,
                    type: refetchType,
                  },
                },
              },
            },
            {
              requestPolicy: 'network-only',
            },
          )
          .toPromise();

        const newProductData = result.data?.packhouseScreen as PackhouseScreen | undefined;

        if (newProductData) {
          const refetchProductIDs =
            refetchType === RefetchType.ProductMixedPallet || refetchType === RefetchType.Product
              ? productIDs
              : undefined;

          setPackhouseData((prev) => stitchScheduleScreen(prev, newProductData, refetchProductIDs));
        }
      } catch (error) {
        showSnackbar({ message: `Error while refreshing products: ${productIDs.join(', ')}` }, SnackbarVariant.ERROR);
      }
    },
    [client, queryVariablesNew, showSnackbar, setPackhouseData, stitchScheduleScreen],
  );

  const handleSetPackhouseDate = useCallback(
    (date: Dayjs) => {
      if (!date.isSame(packhouseStartDate, 'day')) {
        setPackhouseStartDate(date);
        setSearchParam(PACKHOUSE_DATE_PARAM, date.format(PACKHOUSE_DATE_FORMAT), { replace: true });
        handleResetPageVertHoriz();
        stitchPackhouse.clear();
      }
    },
    [packhouseStartDate, handleResetPageVertHoriz, stitchPackhouse, setSearchParam],
  );

  const value = useMemo(
    () => ({
      hasPending: hasPending,
      productID,
      packhouseResult: packhouseResultNew,
      packhouseFetching,
      refreshPackhouseResult: refreshPackhouseResultNew,
      packhouseSchedule,
      packhouseSchedulePageInfos: packhouseSchedule?.allocations?.pageInfos,
      packhouseStartDate,
      searchValue,
      setPackhouseStartDate: handleSetPackhouseDate,
      onSearchChange: handleSearchChange,
      onSubmitAllocation: handleSaveAllocation,
      onRefetchProductData: handleRefetchProductData,
      onIncreasePageVertical: () => setVerticalPageNumber((prev) => prev + 1),
      onIncreasePageHorizontal: () => {
        handleResetPageVert();
        setHorizontalPageNumber((prev) => prev + 1);
      },
      onDecreasePageHorizontal: () => {
        handleResetPageVert();
        setHorizontalPageNumber((prev) => prev - 1);
      },
      onRefetchPackhouse: handleRefetchPackhouse,
      onPackhouseDataChange: setPackhouseData,
      onResetPageVertHoriz: handleResetPageVertHoriz,
      onClearAllInventories: handleClearAllInventories,
    }),
    [
      hasPending,
      productID,
      packhouseResultNew,
      packhouseFetching,
      refreshPackhouseResultNew,
      packhouseSchedule,
      packhouseStartDate,
      searchValue,
      handleSearchChange,
      handleSetPackhouseDate,
      handleSaveAllocation,
      handleRefetchProductData,
      handleResetPageVert,
      handleRefetchPackhouse,
      setPackhouseData,
      handleResetPageVertHoriz,
      handleClearAllInventories,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const usePackhouse = () => {
  const result = useContext(Context);
  if (!result) throw new Error('usePackhouse must be used within a PackhouseProvider');
  return result;
};
